import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  FaArrowRight,
  FaGlobe,
  FaMobileAlt,
  FaSearchPlus,
  FaChevronLeft,
  FaChevronRight,
  FaTimes,
  FaCheckCircle,
  FaBrain,
  FaExternalLinkAlt,
  FaMagic,
  FaListUl,
  FaChartBar,
  FaMicrophone,
  FaArrowLeft,
  FaWifi,
  FaBatteryThreeQuarters
} from 'react-icons/fa';

// ─────────────────────────────────────────────
//  Project Data
// ─────────────────────────────────────────────
const projects = [
  {
    id: 'expense',
    title: 'BCC Expense Tracker',
    subtitle: 'AI-Powered Conversational Budgeting',
    type: 'mobile',
    typeBadge: 'Mobile Application',
    tagline: 'Conversational Personal Finance App',
    description:
      'An intelligent personal finance and budgeting mobile app. Users log expenses in plain conversational language or via audio. The integrated Gemini AI parser translates logs into structured records, updates analytics, and flags budget thresholds — all in real-time.',
    techs: ['Flutter', 'Gemini AI', 'Firebase', 'Dart'],
    features: [
      'Natural Language & voice input powered by Gemini API.',
      'Spending breakdown with category-wise trend indicators.',
      'Firebase Firestore real-time sync for all transactions.',
      'Smart budget thresholds with green/red indicators.',
      'Dynamic charts, progress bars, and transaction history.',
    ],
    stats: [
      { label: 'AI Parse Time', value: '0.8s' },
      { label: 'AI Accuracy', value: '99.4%' },
      { label: 'Framework', value: 'Flutter' },
    ],
    primaryImage: '/portfolio/expense_collage.png',
    gallery: [
      '/portfolio/expense_collage.png',
      '/portfolio/expense_1.png',
      '/portfolio/expense_2.png',
      '/portfolio/expense_3.png',
      '/portfolio/expense_4.png',
    ],
    galleryNames: ['Overview', 'Dashboard', 'Categories', 'AI Input', 'Transactions'],
    accentColor: '#7c3aed',
    accentBg: 'rgba(124,58,237,0.08)',
    liveLink: null,
  },
  {
    id: 'lms',
    title: 'Sharp HRD LMS Website',
    subtitle: 'Enterprise Course Marketplace Platform',
    type: 'web',
    typeBadge: 'Web Platform',
    tagline: 'Sell, Watch & Certify — All in One',
    description:
      'A full-stack Learning Management System where users can browse, enrol, purchase, and stream courses secured by AWS. Instructors get a powerful admin panel, automatic certificate generation, and Razorpay-integrated checkout — all hosted on a Hostinger VPS.',
    techs: ['React', 'Node.js', 'MongoDB', 'AWS S3', 'Razorpay', 'Hostinger VPS'],
    features: [
      'Course marketplace — browse, preview, enrol and purchase courses.',
      'Razorpay payment gateway integration for seamless checkout.',
      'Secure video hosting on AWS S3 with signed URL streaming.',
      'Automatic PDF certificate generation on course completion.',
      'Admin panel for instructors — add, edit, manage courses & students.',
      'Role-based access: learner, instructor, and super-admin tiers.',
    ],
    stats: [
      { label: 'Courses Live', value: '30+' },
      { label: 'Enrolled Users', value: '45k+' },
      { label: 'Uptime', value: '99.9%' },
    ],
    primaryImage: '/portfolio/lms_preview.png',
    gallery: ['/portfolio/lms_preview.png'],
    galleryNames: ['LMS Dashboard Preview'],
    accentColor: '#2563eb',
    accentBg: 'rgba(37,99,235,0.08)',
    liveLink: 'https://lms.sharphrdservice.com/',
  },
];

// ─────────────────────────────────────────────
//  Expense Tracker App Simulator
// ─────────────────────────────────────────────
const CATEGORIES = [
  { name: 'Food & Dining', emoji: '🍜', color: '#7c2d2d', bar: '#ef4444', pct: 53 },
  { name: 'Travel', emoji: '🚗', color: '#1e3a5f', bar: '#3b82f6', pct: 22 },
  { name: 'Utilities', emoji: '⚡', color: '#1a3024', bar: '#22c55e', pct: 15 },
  { name: 'Entertainment', emoji: '🎬', color: '#2d1a4a', bar: '#a855f7', pct: 10 },
];

const INITIAL_TRANSACTIONS = [
  { id: 1, emoji: '🍜', title: 'Dinner', sub: 'Food & Dining • 8:10 PM', amount: -1599 },
  { id: 2, emoji: '🚗', title: 'Cab to Office', sub: 'Travel • 9:00 AM', amount: -350 },
  { id: 3, emoji: '⚡', title: 'Electricity Bill', sub: 'Utilities • Yesterday', amount: -2000 },
];

const parseExpenseInput = (text) => {
  const ltext = text.toLowerCase();
  let amount = 0;
  let category = 'Other 📦';
  let note = text;

  const patterns = [
    /(\d+(?:\.\d+)?)\s*(?:rs|rupees?|inr|₹)/i,
    /(?:rs|rupees?|inr|₹)\s*(\d+(?:\.\d+)?)/i,
    /\b(\d{2,5})\b/,
  ];
  for (const p of patterns) {
    const m = text.match(p);
    if (m) { amount = parseFloat(m[1]); break; }
  }

  if (/coffee|dinner|food|lunch|breakfast|nashta|restaurant|burger|chai|meal/i.test(ltext)) {
    category = 'Food & Dining 🍜';
  } else if (/cab|taxi|metro|travel|fuel|petrol|uber|ola|bus|train/i.test(ltext)) {
    category = 'Travel 🚗';
  } else if (/bill|electricity|wifi|recharge|internet|gas|water/i.test(ltext)) {
    category = 'Utilities ⚡';
  } else if (/movie|netflix|subscription|game|fun|amazon|prime|spotify/i.test(ltext)) {
    category = 'Entertainment 🎬';
  }

  const rawNote = text.replace(/(\d+(?:\.\d+)?)\s*(?:rs|rupees?|inr|₹)/gi, '').replace(/(?:rs|rupees?|inr|₹)\s*(\d+(?:\.\d+)?)/gi, '').replace(/\b(spent|paid|for|on|ka|ke|kiya|kia|ne)\b/gi, '').trim();
  note = rawNote.charAt(0).toUpperCase() + rawNote.slice(1) || text;

  return { amount: amount || 100, category, note };
};

function AppSimulator() {
  const [screen, setScreen] = useState('dash'); // dash | ai | transactions
  const [inputText, setInputText] = useState('');
  const [isParsing, setIsParsing] = useState(false);
  const [parsedResult, setParsedResult] = useState(null);
  const [transactions, setTransactions] = useState(INITIAL_TRANSACTIONS);
  const [totalSpent, setTotalSpent] = useState(7080);
  const inputRef = useRef(null);

  const handleParse = (text) => {
    const t = text || inputText;
    if (!t.trim()) return;
    setIsParsing(true);
    setParsedResult(null);
    setTimeout(() => {
      const result = parseExpenseInput(t);
      setParsedResult(result);
      setIsParsing(false);
    }, 1100);
  };

  const handleSave = () => {
    if (!parsedResult) return;
    const emoji = parsedResult.category.split(' ')[1] || '📦';
    const newTx = {
      id: Date.now(),
      emoji,
      title: parsedResult.note.slice(0, 22),
      sub: `${parsedResult.category.split(' ')[0]} • Just now`,
      amount: -parsedResult.amount,
    };
    setTransactions((prev) => [newTx, ...prev]);
    setTotalSpent((prev) => prev + parsedResult.amount);
    setParsedResult(null);
    setInputText('');
    setScreen('transactions');
  };

  // ── Status bar
  const StatusBar = () => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 18px 4px', fontSize: 11, color: '#fff', fontWeight: 600, letterSpacing: 0.3 }}>
      <span>9:41</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
        <FaWifi size={10} />
        <span style={{ fontSize: 9 }}>5G</span>
        <FaBatteryThreeQuarters size={11} />
      </div>
    </div>
  );

  // ── Bottom nav bar
  const BottomNav = () => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-around', height: 54, background: '#1a1a1a', borderTop: '1px solid #2a2a2a', flexShrink: 0 }}>
      {[
        { id: 'dash', icon: <FaChartBar size={15} />, label: 'Dashboard' },
        { id: 'ai', icon: <FaMagic size={15} />, label: 'AI Log' },
        { id: 'transactions', icon: <FaListUl size={15} />, label: 'History' },
      ].map((tab) => (
        <button
          key={tab.id}
          onClick={() => { setScreen(tab.id); setParsedResult(null); }}
          style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
            background: 'none', border: 'none', cursor: 'pointer',
            color: screen === tab.id ? '#7c3aed' : '#555',
            transition: 'color 0.2s',
          }}
        >
          {tab.icon}
          <span style={{ fontSize: 9, fontWeight: 700 }}>{tab.label}</span>
        </button>
      ))}
    </div>
  );

  // ── Dashboard screen
  const DashScreen = () => (
    <div style={{ flex: 1, overflowY: 'auto', padding: '10px 14px 8px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div>
          <div style={{ color: '#fff', fontWeight: 800, fontSize: 16 }}>Hey, Aditya 👋</div>
          <div style={{ color: '#666', fontSize: 10 }}>July 2026</div>
        </div>
        <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'linear-gradient(135deg, #06b6d4, #3b82f6)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 900, fontSize: 13 }}>A</div>
      </div>

      {/* Total spending card */}
      <div style={{ background: '#1e1e1e', borderRadius: 18, padding: '12px 14px', marginBottom: 10 }}>
        <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
          {['Month', 'Today'].map((t) => (
            <span key={t} style={{ fontSize: 9, color: t === 'Month' ? '#fff' : '#666', background: t === 'Month' ? '#2a2a2a' : 'transparent', padding: '3px 8px', borderRadius: 20, fontWeight: 700 }}>{t}</span>
          ))}
          <span style={{ fontSize: 9, color: '#aaa', background: '#2a2a2a', padding: '3px 8px', borderRadius: 20, marginLeft: 'auto', fontWeight: 600 }}>📅 May 2026</span>
        </div>
        <div style={{ color: '#888', fontSize: 9, marginBottom: 2 }}>Total Spending</div>
        <div style={{ color: '#fff', fontWeight: 900, fontSize: 24, letterSpacing: -1, marginBottom: 4 }}>₹{totalSpent.toLocaleString('en-IN')}.00</div>
        <div style={{ color: '#22c55e', fontSize: 9, fontWeight: 700 }}>↗ 49.7% less than monthly avg</div>
      </div>

      {/* Comparison mini cards */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
        <div style={{ background: '#1a2535', borderRadius: 14, padding: '10px 12px' }}>
          <div style={{ color: '#8899bb', fontSize: 8, fontWeight: 800, letterSpacing: 1, marginBottom: 4 }}>LAST MONTH</div>
          <div style={{ color: '#fff', fontWeight: 800, fontSize: 14 }}>₹34,532</div>
        </div>
        <div style={{ background: '#1e2f55', borderRadius: 14, padding: '10px 12px' }}>
          <div style={{ color: '#7299dd', fontSize: 8, fontWeight: 800, letterSpacing: 1, marginBottom: 4 }}>MONTHLY AVG</div>
          <div style={{ color: '#fff', fontWeight: 800, fontSize: 14 }}>₹14,070</div>
        </div>
      </div>

      {/* Top Spending categories */}
      <div style={{ color: '#fff', fontWeight: 800, fontSize: 12, marginBottom: 8 }}>Top Spending</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        {CATEGORIES.map((cat) => (
          <div key={cat.name} style={{ background: cat.color, borderRadius: 14, padding: '10px 10px 8px' }}>
            <div style={{ fontSize: 16, marginBottom: 4 }}>{cat.emoji}</div>
            <div style={{ color: '#ddd', fontSize: 9, fontWeight: 700, marginBottom: 1 }}>{cat.name}</div>
            <div style={{ color: '#fff', fontWeight: 900, fontSize: 13, marginBottom: 4 }}>₹{(totalSpent * cat.pct / 100).toFixed(0)}</div>
            <div style={{ background: '#00000040', borderRadius: 4, height: 3, overflow: 'hidden' }}>
              <div style={{ width: `${cat.pct}%`, height: '100%', background: cat.bar, borderRadius: 4 }} />
            </div>
            <div style={{ color: '#aaa', fontSize: 8, marginTop: 3, textAlign: 'right' }}>{cat.pct}%</div>
          </div>
        ))}
      </div>

      {/* AI FAB CTA */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12, marginBottom: 4 }}>
        <button onClick={() => setScreen('ai')} style={{ width: 40, height: 40, borderRadius: '50%', background: 'linear-gradient(135deg, #7c3aed, #4f46e5)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 16px #7c3aed55' }}>
          <FaMagic size={14} color="#fff" />
        </button>
      </div>
    </div>
  );

  // ── AI Input screen
  const AIScreen = () => (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#111' }}>
      {/* Top bar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 16px', borderBottom: '1px solid #1e1e1e' }}>
        <button onClick={() => setScreen('dash')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#aaa' }}>
          <FaArrowLeft size={14} />
        </button>
        <span style={{ color: '#fff', fontWeight: 700, fontSize: 13 }}>Expense Note</span>
        <button
          onClick={handleSave}
          disabled={!parsedResult}
          style={{ background: 'none', border: 'none', cursor: parsedResult ? 'pointer' : 'default', color: parsedResult ? '#7c3aed' : '#444', fontWeight: 800, fontSize: 11 }}
        >
          Save
        </button>
      </div>

      {/* Input area */}
      <div style={{ flex: 1, padding: '14px 16px', position: 'relative', display: 'flex', flexDirection: 'column' }}>
        <textarea
          ref={inputRef}
          value={inputText}
          onChange={(e) => { setInputText(e.target.value); setParsedResult(null); }}
          placeholder={'Subah 50 ka nashta kiya,\nOffice cab ke 250, aur dinner ke 1500...'}
          style={{
            flex: 1, background: 'transparent', border: 'none', outline: 'none', resize: 'none',
            color: inputText ? '#fff' : '#444', fontSize: 13, fontFamily: 'inherit', lineHeight: 1.6,
            placeholderColor: '#444',
          }}
          disabled={isParsing}
        />
        <div style={{ color: '#444', fontSize: 9, marginTop: 4 }}>{inputText.length} / 500</div>

        {/* Microphone button */}
        <button
          style={{ position: 'absolute', bottom: 40, right: 14, width: 36, height: 36, borderRadius: '50%', background: '#1e3352', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <FaMicrophone size={14} color="#7eb8f5" />
        </button>
      </div>

      {/* Quick preset chips */}
      <div style={{ padding: '8px 14px', borderTop: '1px solid #1e1e1e' }}>
        <div style={{ color: '#555', fontSize: 9, fontWeight: 700, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 1 }}>Quick presets</div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {['Spent 250 rs on taxi', 'Coffee 150 rupees', 'Netflix 699 rs', 'Groceries 850 inr'].map((p) => (
            <button
              key={p}
              onClick={() => { setInputText(p); handleParse(p); }}
              style={{ fontSize: 8.5, background: '#1e1e1e', border: '1px solid #2a2a2a', color: '#bbb', padding: '4px 9px', borderRadius: 20, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap', fontWeight: 600 }}
            >
              {p}
            </button>
          ))}
        </div>
      </div>

      {/* Parse button */}
      <div style={{ padding: '10px 14px 12px' }}>
        <button
          onClick={() => handleParse()}
          disabled={isParsing || !inputText.trim()}
          style={{
            width: '100%', padding: '11px', borderRadius: 14, border: 'none', cursor: inputText.trim() && !isParsing ? 'pointer' : 'default',
            background: inputText.trim() && !isParsing ? 'linear-gradient(135deg, #7c3aed, #4f46e5)' : '#1e1e1e',
            color: inputText.trim() && !isParsing ? '#fff' : '#444',
            fontWeight: 800, fontSize: 11, fontFamily: 'inherit',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            transition: 'all 0.25s',
            boxShadow: inputText.trim() && !isParsing ? '0 4px 20px #7c3aed44' : 'none',
          }}
        >
          {isParsing ? (
            <>
              <div style={{ width: 12, height: 12, borderRadius: '50%', border: '2px solid #7c3aed', borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite' }} />
              Gemini is parsing...
            </>
          ) : (
            <><FaBrain size={11} /> Parse with Gemini AI</>
          )}
        </button>
      </div>

      {/* Parsed result card */}
      {parsedResult && !isParsing && (
        <div style={{ margin: '0 14px 12px', background: '#0d1f12', border: '1px solid #1a3a20', borderRadius: 14, padding: '10px 12px' }}>
          <div style={{ color: '#22c55e', fontSize: 9, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
            <FaCheckCircle size={9} /> Gemini Parsed Result
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ color: '#fff', fontWeight: 900, fontSize: 16 }}>-₹{parsedResult.amount}</div>
              <div style={{ color: '#888', fontSize: 9, marginTop: 2 }}>{parsedResult.category}</div>
              <div style={{ color: '#aaa', fontSize: 8.5, marginTop: 2, fontStyle: 'italic' }}>"{parsedResult.note.slice(0, 30)}"</div>
            </div>
            <button
              onClick={handleSave}
              style={{ background: '#22c55e', border: 'none', color: '#000', fontWeight: 800, fontSize: 9, padding: '5px 10px', borderRadius: 10, cursor: 'pointer' }}
            >
              Save & Log
            </button>
          </div>
        </div>
      )}
    </div>
  );

  // ── Transactions history screen
  const TxScreen = () => (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '10px 14px 6px', borderBottom: '1px solid #1e1e1e' }}>
        <div style={{ color: '#fff', fontWeight: 800, fontSize: 14 }}>Transaction History</div>
        <div style={{ color: '#555', fontSize: 9, marginTop: 2 }}>All logged expenses</div>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '8px 14px' }}>
        {transactions.map((tx) => (
          <div key={tx.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #1a1a1a' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 34, height: 34, borderRadius: 10, background: '#1e1e1e', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>{tx.emoji}</div>
              <div>
                <div style={{ color: '#fff', fontWeight: 700, fontSize: 11 }}>{tx.title}</div>
                <div style={{ color: '#555', fontSize: 8.5, marginTop: 1 }}>{tx.sub}</div>
              </div>
            </div>
            <div style={{ color: '#ef4444', fontWeight: 800, fontSize: 12 }}>₹{Math.abs(tx.amount)}</div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div style={{
      width: 270, height: 540,
      background: '#111',
      borderRadius: 38,
      border: '8px solid #222',
      boxShadow: '0 30px 80px rgba(0,0,0,0.7), inset 0 0 0 1px #333',
      display: 'flex', flexDirection: 'column',
      overflow: 'hidden',
      fontFamily: "'Inter', system-ui, sans-serif",
      color: '#fff',
      position: 'relative',
    }}>
      {/* Dynamic island */}
      <div style={{ position: 'absolute', top: 10, left: '50%', transform: 'translateX(-50%)', width: 80, height: 22, background: '#000', borderRadius: 11, zIndex: 10 }} />
      <StatusBar />
      {screen === 'dash' && <DashScreen />}
      {screen === 'ai' && <AIScreen />}
      {screen === 'transactions' && <TxScreen />}
      <BottomNav />

      {/* Spin animation */}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ─────────────────────────────────────────────
//  Main Portfolio Page
// ─────────────────────────────────────────────
export default function Portfolio() {
  const [activeProject, setActiveProject] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxGallery, setLightboxGallery] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxNames, setLightboxNames] = useState([]);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const openDrawer = (project) => {
    setActiveProject(project);
    setDrawerOpen(true);
    document.body.style.overflow = 'hidden';
  };
  const closeDrawer = () => {
    setDrawerOpen(false);
    document.body.style.overflow = '';
  };
  const openLightbox = (gallery, index, names) => {
    setLightboxGallery(gallery);
    setLightboxIndex(index);
    setLightboxNames(names || []);
    setLightboxOpen(true);
  };
  const closeLightbox = () => setLightboxOpen(false);
  const nextImg = (e) => { e.stopPropagation(); setLightboxIndex((p) => (p + 1) % lightboxGallery.length); };
  const prevImg = (e) => { e.stopPropagation(); setLightboxIndex((p) => (p - 1 + lightboxGallery.length) % lightboxGallery.length); };

  useEffect(() => {
    const fn = (e) => {
      if (e.key === 'Escape') { if (lightboxOpen) closeLightbox(); else if (drawerOpen) closeDrawer(); }
      if (lightboxOpen) { if (e.key === 'ArrowRight') nextImg(e); if (e.key === 'ArrowLeft') prevImg(e); }
    };
    window.addEventListener('keydown', fn);
    return () => window.removeEventListener('keydown', fn);
  }, [lightboxOpen, drawerOpen, lightboxGallery]);

  return (
    <div className="min-h-screen relative" style={{ background: 'var(--bg-primary)' }}>

      {/* ── BACKGROUND FX ── */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden" style={{ zIndex: 0 }}>
        <div className="absolute -top-40 -left-40 w-[700px] h-[700px] rounded-full opacity-[0.06]" style={{ background: 'radial-gradient(circle, #7c3aed, transparent 70%)', filter: 'blur(60px)' }} />
        <div className="absolute top-1/2 -right-40 w-[600px] h-[600px] rounded-full opacity-[0.05]" style={{ background: 'radial-gradient(circle, #2563eb, transparent 70%)', filter: 'blur(80px)' }} />
        <div className="absolute bottom-0 left-1/3 w-[500px] h-[500px] rounded-full opacity-[0.04]" style={{ background: 'radial-gradient(circle, #7c3aed, transparent 70%)', filter: 'blur(60px)' }} />
      </div>

      {/* ════════════════════════════════════════
          HERO SECTION
      ════════════════════════════════════════ */}
      <section className="relative z-10 min-h-[75vh] flex flex-col justify-center px-6 pt-28 pb-20 md:pt-36 md:pb-24 max-w-7xl mx-auto">

        <div className="max-w-5xl">
          {/* Eyebrow */}
          <div className="flex items-center gap-3 mb-8">
            <div className="h-px w-8 bg-slate-400/40" />
            <span className="text-[10px] font-black uppercase tracking-[0.25em] text-slate-400">Portfolio — Selected Works</span>
          </div>

          {/* Main headline */}
          <h1 className="font-black leading-[1.05] tracking-tight mb-8 text-slate-900 dark:text-white" style={{ fontSize: 'clamp(2.8rem, 7vw, 5.5rem)' }}>
            Products built<br />
            <span className="relative inline-block">
              <span className="gradient-text-purple">for real impact.</span>
            </span>
          </h1>

          {/* Sub copy */}
          <p className="text-lg leading-relaxed text-slate-500 dark:text-zinc-400 max-w-2xl">
            End-to-end digital products — from AI-powered mobile apps to enterprise learning platforms. Built with precision, shipped to production.
          </p>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-6 left-6 hidden md:flex items-center gap-2 text-slate-400">
          <div className="w-4 h-7 rounded-full border-2 border-slate-300 dark:border-zinc-600 flex items-start justify-center pt-1.5">
            <div className="w-1 h-2 rounded-full bg-slate-400" style={{ animation: 'scrollDot 1.8s ease-in-out infinite' }} />
          </div>
          <span className="text-[9px] font-bold uppercase tracking-widest">Scroll to explore</span>
        </div>
        <style>{`@keyframes scrollDot { 0%,100%{transform:translateY(0);opacity:1} 60%{transform:translateY(6px);opacity:0.3} }`}</style>
      </section>

      {/* ════════════════════════════════════════
          PROJECT GRID — Partitioned with different BG and top border
      ════════════════════════════════════════ */}
      <section className="relative z-10 border-t py-20 md:py-28" style={{ background: 'var(--bg-surface)', borderColor: 'var(--border-color)' }}>
        <main className="max-w-7xl mx-auto px-6 space-y-8">

        {/* ── FEATURED: LMS Website (full-width) ── */}
        {(() => {
          const project = projects.find(p => p.id === 'lms');
          return (
            <article
              onClick={() => openDrawer(project)}
              className="group cursor-pointer rounded-3xl overflow-hidden border relative flex flex-col md:flex-row transition-all duration-500 hover:-translate-y-1 hover:shadow-2xl"
              style={{ border: '1px solid var(--border-color)', background: 'var(--bg-card)', minHeight: 360 }}
            >
              {/* Left: Text content */}
              <div className="flex flex-col justify-between p-8 md:p-10 md:w-[38%] flex-shrink-0 border-b md:border-b-0 md:border-r" style={{ borderColor: 'var(--border-color)' }}>
                <div>
                  {/* Featured badge */}
                  <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest mb-6 border" style={{ borderColor: 'var(--border-color)', color: 'var(--text-secondary)', background: 'var(--bg-primary)' }}>
                    <span className="w-1.5 h-1.5 rounded-full bg-green-400" />
                    Featured Project
                  </div>
                  <div className="text-[10px] font-extrabold uppercase tracking-widest text-slate-400 mb-2">{project.subtitle}</div>
                  <h2 className="text-3xl md:text-4xl font-black text-slate-900 dark:text-white leading-tight tracking-tight mb-4">{project.title}</h2>
                  <p className="text-sm text-slate-500 dark:text-zinc-400 leading-relaxed line-clamp-4">{project.description}</p>
                </div>
                <div className="mt-8">
                  <div className="flex flex-wrap gap-1.5 mb-6">
                    {project.techs.slice(0, 4).map(t => (
                      <span key={t} className="px-2.5 py-1 rounded-lg text-[10px] font-semibold border text-slate-600 dark:text-zinc-300" style={{ borderColor: 'var(--border-color)', background: 'var(--bg-primary)' }}>{t}</span>
                    ))}
                    {project.techs.length > 4 && <span className="px-2.5 py-1 rounded-lg text-[10px] font-semibold border text-slate-400" style={{ borderColor: 'var(--border-color)', background: 'var(--bg-primary)' }}>+{project.techs.length - 4}</span>}
                  </div>
                  <div className="flex items-center gap-2 text-sm font-bold text-slate-900 dark:text-white group-hover:gap-3 transition-all">
                    Explore Case Study <FaArrowRight size={12} />
                  </div>
                </div>
              </div>

              {/* Right: Image */}
              <div className="relative flex-grow overflow-hidden" style={{ minHeight: 260 }}>
                {/* Browser chrome strip */}
                <div className="h-7 px-3 flex items-center gap-1.5 flex-shrink-0 border-b" style={{ background: 'var(--bg-primary)', borderColor: 'var(--border-color)' }}>
                  <span className="w-2 h-2 rounded-full bg-red-400/70" />
                  <span className="w-2 h-2 rounded-full bg-yellow-400/70" />
                  <span className="w-2 h-2 rounded-full bg-green-400/70" />
                  <div className="h-4 rounded text-[7px] px-2 flex items-center mx-auto truncate opacity-50" style={{ background: 'var(--bg-card)', color: 'var(--text-muted)', border: '1px solid var(--border-color)', width: '38%' }}>
                    🔒 lms.sharphrdservice.com
                  </div>
                </div>
                <div className="relative overflow-hidden" style={{ height: 'calc(100% - 28px)' }}>
                  <img
                    src={project.primaryImage}
                    alt={project.title}
                    className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.35)' }}>
                    <span className="px-5 py-2.5 rounded-full text-xs font-bold bg-white text-black shadow-lg flex items-center gap-2">
                      Open Case Study <FaArrowRight size={10} />
                    </span>
                  </div>
                </div>
                {/* Type badge */}
                <div className="absolute top-10 right-3 px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-wider text-white flex items-center gap-1.5" style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.1)' }}>
                  <FaGlobe size={8} style={{ color: '#93c5fd' }} />
                  {project.typeBadge}
                </div>
              </div>
            </article>
          );
        })()}

        {/* ── Secondary cards row ── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {projects.filter(p => p.id !== 'lms').map((project) => (
            <article
              key={project.id}
              onClick={() => openDrawer(project)}
              className="group cursor-pointer flex flex-col rounded-3xl overflow-hidden border transition-all duration-500 hover:-translate-y-1 hover:shadow-2xl"
              style={{ border: '1px solid var(--border-color)', background: 'var(--bg-card)' }}
            >
              {/* Preview image */}
              <div className="relative w-full overflow-hidden" style={{ aspectRatio: '16/9', borderBottom: '1px solid var(--border-color)' }}>
                <img
                  src={project.primaryImage}
                  alt={project.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ background: 'rgba(0,0,0,0.4)' }}>
                  <span className="px-5 py-2.5 rounded-full text-xs font-bold bg-white text-black shadow-lg flex items-center gap-2">
                    Open Case Study <FaArrowRight size={10} />
                  </span>
                </div>
                <div className="absolute top-3 left-3 px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-wider text-white flex items-center gap-1.5" style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.1)' }}>
                  <FaMobileAlt size={9} style={{ color: '#c4b5fd' }} />
                  {project.typeBadge}
                </div>
              </div>

              {/* Card info */}
              <div className="p-6 flex flex-col flex-grow">
                <span className="text-[10px] font-extrabold uppercase tracking-widest text-slate-400">{project.subtitle}</span>
                <h3 className="text-xl font-black mt-1.5 mb-3 text-slate-900 dark:text-white">{project.title}</h3>
                <p className="text-xs leading-relaxed text-slate-500 dark:text-zinc-400 line-clamp-2 flex-grow">{project.description}</p>
                <div className="mt-5 pt-5 border-t flex items-center justify-between" style={{ borderColor: 'var(--border-color)' }}>
                  <div className="flex gap-1 flex-wrap">
                    {project.techs.slice(0, 3).map(t => <span key={t} className="tech-pill font-mono text-[9px]">{t}</span>)}
                    {project.techs.length > 3 && <span className="tech-pill font-mono text-[9px]">+{project.techs.length - 3}</span>}
                  </div>
                  <span className="text-xs font-bold flex items-center gap-1 text-slate-500 dark:text-zinc-400 group-hover:gap-2 transition-all">
                    Details <FaArrowRight size={10} className="group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </div>
            </article>
          ))}

          {/* Card 3: Your Project Next (CTA to Contact Us) */}
          <Link
            to="/contactus"
            className="group flex flex-col rounded-3xl overflow-hidden border transition-all duration-500 hover:-translate-y-1 hover:shadow-2xl hover:border-purple-500/50"
            style={{
              border: '1.5.px dashed var(--border-color)',
              background: 'var(--bg-card)',
            }}
          >
            {/* Visual Preview Window (Dashed grid wireframe) */}
            <div className="relative w-full overflow-hidden flex-shrink-0 flex items-center justify-center bg-zinc-950/5 dark:bg-black/20 border-b border-dashed" style={{ aspectRatio: '16/9', borderColor: 'var(--border-color)' }}>
              {/* Tech drawing grid lines */}
              <div className="absolute inset-0 opacity-[0.05] dark:opacity-[0.08]"
                   style={{
                     backgroundImage: 'linear-gradient(to right, currentColor 1px, transparent 1px), linear-gradient(to bottom, currentColor 1px, transparent 1px)',
                     backgroundSize: '24px 24px'
                   }}
              />
              
              {/* Drawing shape mock */}
              <div className="relative w-28 h-16 rounded-xl border border-dashed border-slate-400 dark:border-zinc-700 flex items-center justify-center">
                <div className="w-8 h-8 rounded-full border border-dashed border-slate-400 dark:border-zinc-700 flex items-center justify-center">
                  <span className="w-2.5 h-2.5 rounded-full bg-purple-500 dark:bg-purple-400 animate-ping absolute" />
                  <span className="w-2 h-2 rounded-full bg-purple-600 dark:bg-purple-500" />
                </div>
              </div>

              {/* Status Badge */}
              <div className="absolute top-3 left-3 px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-wider text-purple-600 dark:text-purple-400" style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-color)' }}>
                🎯 Design & Launch
              </div>
            </div>

            {/* Card info */}
            <div className="p-6 flex flex-col justify-between flex-grow">
              <div>
                <span className="text-[10px] font-extrabold uppercase tracking-widest text-slate-400">Collaboration Space</span>
                <h3 className="text-xl font-black mt-1.5 mb-3 text-slate-909 dark:text-white group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors">
                  Your Project Next
                </h3>
                <p className="text-xs leading-relaxed text-slate-500 dark:text-zinc-400">
                  Have an idea for a web app, mobile application, SaaS solution, or AI product integration? Let's engineer a high-performance solution together.
                </p>
              </div>
              <div className="mt-5 pt-5 border-t flex items-center justify-between" style={{ borderColor: 'var(--border-color)' }}>
                <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400">Status: Available</span>
                <span className="text-xs font-bold flex items-center gap-1 text-purple-600 dark:text-purple-400 group-hover:gap-2 transition-all">
                  Get in touch <FaArrowRight size={10} className="group-hover:translate-x-1 transition-transform" />
                </span>
              </div>
            </div>
          </Link>
        </div>
      </main>
      </section>

      {/* ── Right Drawer ── */}
      {activeProject && (
        <div className={`fixed inset-0 flex justify-end ${drawerOpen ? 'pointer-events-auto' : 'pointer-events-none'}`} style={{ zIndex: 1001 }}>
          <div onClick={closeDrawer} className={`absolute inset-0 transition-opacity duration-300 ${drawerOpen ? 'opacity-100' : 'opacity-0'}`} style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)' }} />
          <div
            className="relative z-10 w-full lg:w-[960px] xl:w-[1100px] h-full flex flex-col border-l transition-transform duration-300 ease-out"
            style={{ transform: drawerOpen ? 'translateX(0)' : 'translateX(100%)', background: 'var(--bg-card)', borderColor: 'var(--border-color)' }}
          >
            {/* ── Drawer Header with gradient accent strip ── */}
            <div className="flex-shrink-0 border-b" style={{ borderColor: 'var(--border-color)' }}>
              {/* Thin gradient top strip */}
              <div className="h-0.5 w-full" style={{ background: `linear-gradient(90deg, ${activeProject.accentColor}, transparent 70%)` }} />
              <div className="h-14 px-6 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: activeProject.accentBg }}>
                    {activeProject.id === 'expense' ? <FaMobileAlt size={13} style={{ color: activeProject.accentColor }} /> : <FaGlobe size={13} style={{ color: activeProject.accentColor }} />}
                  </div>
                  <div>
                    <div className="text-[9px] font-black uppercase tracking-widest text-slate-400">Case Study</div>
                    <div className="text-sm font-black text-slate-900 dark:text-white leading-tight">{activeProject.title}</div>
                  </div>
                </div>
                <button
                  onClick={closeDrawer}
                  className="w-8 h-8 rounded-full border flex items-center justify-center transition-all hover:rotate-90 duration-300"
                  style={{ borderColor: 'var(--border-color)', color: 'var(--text-secondary)' }}
                >
                  <FaTimes size={12} />
                </button>
              </div>
            </div>

            {/* ── Drawer Body ── */}
            <div className="flex-grow overflow-y-auto" style={{ scrollbarWidth: 'thin' }}>
              <div className="grid grid-cols-1 lg:grid-cols-2">

                {/* ════ LEFT COLUMN — PROJECT INFO ════ */}
                <div className="border-r" style={{ borderColor: 'var(--border-color)' }}>

                  {/* Hero Banner — neutral, no per-project color */}
                  <div className="px-8 pt-10 pb-8 border-b" style={{ borderColor: 'var(--border-color)' }}>
                    <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest mb-4 border" style={{ color: 'var(--text-secondary)', borderColor: 'var(--border-color)', background: 'var(--bg-primary)' }}>
                      {activeProject.id === 'expense' ? <FaMobileAlt size={8} /> : <FaGlobe size={8} />}
                      {activeProject.typeBadge}
                    </div>
                    <h2 className="text-2xl font-black tracking-tight leading-snug mb-3 text-slate-900 dark:text-white">
                      {activeProject.tagline}
                    </h2>
                    <p className="text-sm leading-relaxed text-slate-500 dark:text-zinc-400 max-w-sm">
                      {activeProject.description}
                    </p>
                  </div>

                  {/* Stat Cards Row — only for mobile app, not web */}
                  {activeProject.id === 'expense' && (
                    <div className="px-8 py-6 border-b" style={{ borderColor: 'var(--border-color)' }}>
                      <div className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-3">Benchmarks</div>
                      <div className="grid grid-cols-3 gap-3">
                        {activeProject.stats.map((s) => (
                          <div
                            key={s.label}
                            className="rounded-2xl p-4 flex flex-col gap-1 border"
                            style={{ borderColor: 'var(--border-color)', background: 'var(--bg-primary)' }}
                          >
                            <div className="text-xl font-black tracking-tight leading-none text-slate-900 dark:text-white">{s.value}</div>
                            <div className="text-[9px] font-bold text-slate-400 uppercase tracking-wider leading-tight">{s.label}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Numbered Feature List — neutral */}
                  <div className="px-8 py-6">
                    <div className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-4">Core Capabilities</div>
                    <ol className="space-y-3.5">
                      {activeProject.features.map((f, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <div
                            className="flex-shrink-0 w-5 h-5 rounded-md flex items-center justify-center text-[9px] font-black mt-0.5"
                            style={{ background: 'var(--bg-primary)', color: 'var(--text-secondary)', border: '1px solid var(--border-color)' }}
                          >
                            {i + 1}
                          </div>
                          <span className="text-xs leading-relaxed text-slate-600 dark:text-zinc-300 pt-0.5">{f}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                </div>

                {/* ════ RIGHT COLUMN — Simulator / Web Preview ════ */}
                <div className="flex flex-col items-center justify-start p-8 gap-5">
                  {activeProject.id === 'expense' ? (
                    <>
                      <div className="text-center">
                        <div className="text-[10px] font-black uppercase tracking-widest mb-1 text-slate-400">Live App Simulator</div>
                        <div className="text-[9px] text-slate-400 italic">Faithful recreation — try the AI parser</div>
                      </div>
                      <AppSimulator />

                      {/* Tech stack for Expense below simulator */}
                      <div className="w-full pt-5 border-t" style={{ borderColor: 'var(--border-color)' }}>
                        <div className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-3">Specification & Tech Stack</div>
                        <div className="flex flex-wrap gap-2">
                          {activeProject.techs.map((t) => (
                            <span
                              key={t}
                              className="px-3 py-1.5 rounded-xl text-[10px] font-semibold border transition-all duration-300 hover:border-slate-400 dark:hover:border-zinc-500 cursor-default flex items-center gap-1.5"
                              style={{
                                borderColor: 'var(--border-color)',
                                background: 'var(--bg-primary)',
                                color: 'var(--text-primary)',
                              }}
                            >
                              <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 dark:bg-indigo-400" />
                              {t}
                            </span>
                          ))}
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="w-full">
                        <div className="text-[10px] font-black uppercase tracking-widest mb-1 text-slate-400">Website Preview</div>
                        <div className="text-[9px] text-slate-400 italic mb-4">Hover screenshot to reveal live link overlay</div>
                        {/* Browser chrome mock */}
                        <div className="border rounded-2xl overflow-hidden shadow-lg transition-shadow duration-300 hover:shadow-xl" style={{ borderColor: 'var(--border-color)' }}>
                          {/* Chrome bar */}
                          <div className="h-8 px-3 flex items-center gap-1.5 flex-shrink-0" style={{ background: 'var(--bg-primary)', borderBottom: '1px solid var(--border-color)' }}>
                            <span className="w-2.5 h-2.5 rounded-full bg-red-400/70" />
                            <span className="w-2.5 h-2.5 rounded-full bg-yellow-400/70" />
                            <span className="w-2.5 h-2.5 rounded-full bg-green-400/70" />
                            <div className="h-5 rounded-lg text-[8px] px-2.5 flex items-center gap-1 mx-auto truncate" style={{ background: 'var(--bg-card)', color: 'var(--text-muted)', border: '1px solid var(--border-color)', width: '62%', opacity: 0.8 }}>
                              🔒 lms.sharphrdservice.com
                            </div>
                          </div>
                          {/* Screenshot — hover shows Visit Site */}
                          <a
                            href={activeProject.liveLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group/wp relative block overflow-hidden cursor-pointer"
                            style={{ background: '#f8fafc', maxHeight: 420, overflowY: 'auto' }}
                          >
                            <img
                              src={activeProject.primaryImage}
                              alt={activeProject.title}
                              className="w-full object-contain transition-all duration-500 group-hover/wp:brightness-75"
                              style={{ display: 'block' }}
                            />
                            {/* Visit Site overlay on hover */}
                            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover/wp:opacity-100 transition-opacity duration-300">
                              <div
                                className="flex items-center gap-2.5 px-6 py-3 rounded-2xl font-bold text-sm text-white shadow-2xl transition-transform duration-300 group-hover/wp:scale-105"
                                style={{ background: 'rgba(15,15,15,0.85)', backdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.15)' }}
                              >
                                Visit Live Site <FaExternalLinkAlt size={11} className="text-blue-400" />
                              </div>
                            </div>
                          </a>
                        </div>
                      </div>

                      {/* Tech stack beneath thumbnail */}
                      <div className="w-full border-t pt-5" style={{ borderColor: 'var(--border-color)' }}>
                        <div className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-3">Specification & Tech Stack</div>
                        <div className="flex flex-wrap gap-2">
                          {activeProject.techs.map((t) => (
                            <span
                              key={t}
                              className="px-3 py-1.5 rounded-xl text-[10px] font-semibold border transition-all duration-300 hover:border-slate-400 dark:hover:border-zinc-500 cursor-default flex items-center gap-1.5"
                              style={{
                                borderColor: 'var(--border-color)',
                                background: 'var(--bg-primary)',
                                color: 'var(--text-primary)',
                              }}
                            >
                              <span className="w-1.5 h-1.5 rounded-full bg-blue-500 dark:bg-blue-400" />
                              {t}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* Visit Site CTA beneath tech stack */}
                      <div className="w-full">
                        <a
                          href={activeProject.liveLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group/cta relative flex items-center justify-center gap-2.5 w-full py-4 rounded-2xl text-sm font-semibold overflow-hidden transition-all duration-300 shadow-sm border hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0 text-white"
                          style={{
                            background: 'linear-gradient(135deg, #1e293b, #0f172a)',
                            borderColor: 'rgba(255,255,255,0.06)',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                          }}
                        >
                          {/* Inner glow outline */}
                          <span className="absolute inset-0 rounded-2xl border border-white/5 pointer-events-none" />
                          {/* Shimmer sweep */}
                          <span className="absolute inset-0 translate-x-[-100%] group-hover/cta:translate-x-[100%] transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/10 to-transparent pointer-events-none" />
                          Visit Live Website <FaExternalLinkAlt size={10} className="group-hover/cta:translate-x-0.5 group-hover/cta:-translate-y-0.5 transition-transform" />
                        </a>
                      </div>
                    </>
                  )}
                </div>

              </div>
            </div>
          </div>
        </div>
      )}


      {/* ── Lightbox ── */}
      {lightboxOpen && (
        <div className="fixed inset-0 flex flex-col items-center justify-center" style={{ zIndex: 1002, background: 'rgba(0,0,0,0.97)', backdropFilter: 'blur(12px)' }} onClick={closeLightbox}>
          <div className="absolute top-0 left-0 right-0 h-16 flex items-center justify-between px-6 z-20" style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
            <div>
              <div className="text-[9px] text-slate-400 uppercase font-mono tracking-wider">{lightboxGallery.length > 1 ? `${lightboxIndex + 1} / ${lightboxGallery.length}` : 'Fullscreen'}</div>
              <div className="text-sm font-bold text-white">{lightboxNames[lightboxIndex]}</div>
            </div>
            <button onClick={closeLightbox} className="w-10 h-10 rounded-full flex items-center justify-center text-white" style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.1)' }}>
              <FaTimes size={14} />
            </button>
          </div>
          <div className="w-full max-w-5xl h-[70vh] flex items-center justify-center p-4 relative" onClick={(e) => e.stopPropagation()}>
            <img src={lightboxGallery[lightboxIndex]} alt="" className="max-w-full max-h-full object-contain rounded-xl shadow-2xl" style={{ border: '1px solid rgba(255,255,255,0.08)' }} />
            {lightboxGallery.length > 1 && <>
              <button onClick={prevImg} className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full flex items-center justify-center text-white transition-all hover:-translate-x-1" style={{ background: 'rgba(0,0,0,0.6)', border: '1px solid rgba(255,255,255,0.1)' }}>
                <FaChevronLeft size={14} />
              </button>
              <button onClick={nextImg} className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full flex items-center justify-center text-white transition-all hover:translate-x-1" style={{ background: 'rgba(0,0,0,0.6)', border: '1px solid rgba(255,255,255,0.1)' }}>
                <FaChevronRight size={14} />
              </button>
            </>}
          </div>
          {lightboxGallery.length > 1 && (
            <div className="absolute bottom-6 flex gap-2" onClick={(e) => e.stopPropagation()}>
              {lightboxGallery.map((img, i) => (
                <button key={i} onClick={() => setLightboxIndex(i)} className="w-10 h-10 rounded-lg overflow-hidden border-2 transition-all" style={{ borderColor: i === lightboxIndex ? '#7c3aed' : 'rgba(255,255,255,0.2)', transform: i === lightboxIndex ? 'scale(1.1)' : 'scale(1)' }}>
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

